/**
 * One word per concept on operator screens (C4 §5), and the one place that
 * turns API codes into Mongolian. `__tests__/glossary-lint.test.ts` and
 * `english-lint.test.ts` keep the replaced words / English from coming back.
 */
export const T = {
  staff: "Ажилтан",
  staffPlural: "Ажилтнууд",
  room: "Өрөө",
  equipment: "Тоног төхөөрөмж",
  whoAll: "Ажилтан, өрөө, тоног төхөөрөмж",
  sharedLimit: "Хуваалцсан хязгаар",
  status: "Төлөв",
  published: "Нийтлэгдсэн",
  unpublished: "Идэвхгүй",
  email: "Имэйл",
  reminders: "Сануулга",
  workHours: "Ажлын цаг",
  /** Who made a booking at the front desk / on a kiosk. */
  createdBy: "Бүртгэсэн",
  kiosk: "Киоск",
  note: "Тэмдэглэл",
  /** Bookings made together back to back (several services, one visit). */
  oneVisit: "Нэг айлчлал",
  nextService: "+ Дараагийн үйлчилгээ",
  moveVisit: "Бүх айлчлалыг зөөх",
} as const;

const EVENTS: Record<string, string> = {
  BOOKING_CONFIRMED: "Захиалга баталгаажсан",
  BOOKING_CANCELLED: "Захиалга цуцлагдсан",
  BOOKING_RESCHEDULED: "Цаг өөрчлөгдсөн",
  BOOKING_PAYMENT_RECEIVED: "Төлбөр хүлээн авлаа",
  BOOKING_REMINDER: "Сануулга",
  BOOKING_REMINDER_24H: "24 цагийн өмнө сануулга",
  BOOKING_AWAITING_PAYMENT: "Төлбөр хүлээж буй",
  BOOKING_HOLD_OVERDUE: "Төлбөрийн хугацаа хэтэрсэн",
  COHORT_OCCURRENCE_CANCELLED: "Ангийн хичээл цуцлагдсан",
  PASSWORD_RESET: "Нууц үг сэргээх",
  EMAIL_VERIFICATION: "Имэйл баталгаажуулах",
};

/** Notification event name; unknown codes read «Бусад мэдэгдэл». */
export function eventLabel(event: string): string {
  return EVENTS[event] ?? "Бусад мэдэгдэл";
}

const UNIT: Record<string, string> = { m: "минутын", h: "цагийн", d: "өдрийн", w: "долоо хоногийн" };

/** Reminder fire time: `immediate` → «Шууд», `-24h` → «24 цагийн өмнө». Unparseable → as is. */
export function fireTimeLabel(fireAt: string): string {
  if (fireAt === "immediate") return "Шууд";
  const m = /^([+-])(\d+)([mhdw])$/.exec(fireAt);
  if (!m) return fireAt;
  return `${m[2]} ${UNIT[m[3]]} ${m[1] === "-" ? "өмнө" : "дараа"}`;
}

const pick = (map: Record<string, string>) => (code: string) => map[String(code).toUpperCase()] ?? code;

/** «Хэрэглэгч ажилтан сонгох уу?» choices (any staff). */
export const staffChoiceLabel = pick({
  REQUIRED: "Сонгоно",
  OPTIONAL: "Сонгоно эсвэл «Хэн ч хамаагүй»",
  NONE: "Сонгохгүй",
});

export const templateStatusLabel = pick({ PUBLISHED: "Нийтлэгдсэн", DRAFT: "Ноорог", ARCHIVED: "Архивласан" });
export const deliveryStatusLabel = pick({
  SENT: "Илгээсэн",
  DELIVERED: "Хүрсэн",
  FAILED: "Амжилтгүй",
  PENDING: "Хүлээгдэж буй",
  SKIPPED: "Алгассан",
});
/** Brand names stay as they are. */
export const channelLabel = pick({ EMAIL: "Имэйл", MESSENGER: "Messenger", INSTAGRAM: "Instagram" });
